/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import { SubmitHandler, FieldValues, FormProvider } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { AgentRequest } from '@marageti/z4-sdk/lib/people';
import { useSnackbar } from 'notistack';
import { RoleName } from '@marageti/z4-sdk/lib/auth';
import { useForm, useApiClient, useFetch } from '@marageti/z4-lib';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Partner } from '@marageti/z4-sdk/lib/travel';
import InviteSpecialistFormView from './invite-specialist-view';
import { ContainerProps, RoleOption } from './types';

const roleOptions = [
  { label: 'Manager / Admin', value: RoleName.AGENT_ADMIN },
  { label: 'Specialist', value: RoleName.AGENT },
  { label: 'Support', value: RoleName.AGENT_SUPPORT },
] as RoleOption[];

const InviteSpecialistFormContainer = ({
  open,
  onClose,
}: ContainerProps) => {
  const apiClient = useApiClient();
  const schema = z.object({
    email: z.string().email('Please enter a valid email address').superRefine(async (email, ctx) => {
      if (email && email.includes('@')) {
        const alreadyExists = await apiClient.authClient.userExists(email);
        if (alreadyExists) {
          ctx.addIssue({
            code: 'custom',
            message: 'This email is already registered.',
          });
        }
      }
    }),
    givenName: z.string().min(1, 'Please enter a first name'),
    familyName: z.string().min(1, 'Please enter a last name'),
    role: z.enum(roleOptions.map((option) => option.value) as [string, ...string[]]),
  });

  const { partnerId } = useParams();
  const [submissionError, setSubmissionError] = useState<ApiError>();
  const { mutate: mutatePartner } = useFetch<Partner>(apiClient.partnersClient.getPartner, [partnerId], !!partnerId);

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const formMethods = useForm({
    resolver: zodResolver(schema),
  });

  const handleClose = () => {
    formMethods.reset();
    setSubmissionError(undefined);
    onClose();
  };

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    if (!partnerId) {
      return;
    }
    setSubmissionError(undefined);
    const {
      email,
      givenName,
      familyName,
      role,
    } = data;
    const agent: AgentRequest = {
      partnerId,
      email,
      givenName,
      familyName,
      userInfo: [{
        roles: [role],
      }],
    };
    try {
      const exists = await apiClient.peopleClient.agentExists(email);

      if (!exists) {
        await apiClient.peopleClient.postAgent(agent);
      }

      await apiClient.authClient.postUserReset(email);

      mutatePartner();
      enqueueSnackbar('Invite sent!', { variant: 'success' });
      handleClose();
    } catch (error: any) {
      if (error.code === 401) {
        navigate('/logout');
      } else {
        setSubmissionError(error);
      }
    }
  };

  return (
    <FormProvider {...formMethods}>
      <InviteSpecialistFormView
        open={open}
        onCancel={handleClose}
        onSubmit={onSubmit}
        submissionError={submissionError}
        roleOptions={roleOptions}
      />
    </FormProvider>
  );
};

export default InviteSpecialistFormContainer;
